import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  adminListInstitutionsBasic,
  adminSendNotification,
  adminGetPushBroadcasts,
  adminGetFieldOfStudy,
  adminGetAcademicYears,
  adminGetSchoolGrades,
  AdminNotificationPayload,
} from '../../utils/remote';
import { toast } from 'react-hot-toast';
import { Badge, Button, ConfirmDialog, EmptyState, ErrorPanel, FormField, LoadingState, PagedFooter, PageHeader, PanelCard, PillTabs, Segmented, Table, Td, Th } from '../../school_panel/components/ui';
import { fieldClass } from '../../school_panel/lib/fieldClass';
import { Bell, Check, Send } from 'lucide-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import LogoMark from '../../images/logo/logo.png';
import type { AdminPushBroadcast, AdminPushStatus } from '../../types/admin';
import { withTime } from '../../school_panel/lib/dates';

type TargetCategory = 'all' | 'schools' | 'universities' | 'individual';
type Action = '' | 'open_url' | 'navigate';
type Gender = '' | 'male' | 'female';
type Sub = '' | 'subscribed' | 'unsubscribed';

const TITLE_MAX = 65;
const BODY_MAX = 240;

const INPUT = fieldClass;

const SCREENS: [string, string][] = [
  ['home', 'Dashboard (Home)'],
  ['profile', 'User Profile'],
  ['store-national', 'National Exam Store'],
  ['store-stream', 'Department Store'],
  ['flashcards', 'My Flashcards'],
  ['saved-notes', 'My AI Notes'],
  ['fav', 'Favorite Questions'],
  ['banks', 'Question Banks'],
  ['payment-list', 'Payment History'],
  ['referral', 'Referral Program'],
  ['random-set', 'Random Quiz Settings'],
  ['contact', 'Contact Support'],
];

type HistoryFilter = '' | AdminPushStatus;
const HISTORY_PAGE_SIZE = 10;
const HISTORY_KEY = 'admin-push-broadcasts';
const STATUS_BADGE: Record<AdminPushStatus, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  sent: { label: 'Sent', tone: 'success' },
  partial: { label: 'Partial', tone: 'warning' },
  failed: { label: 'Failed', tone: 'danger' },
  no_recipients: { label: 'No recipients', tone: 'neutral' },
};

const actionLine = (data: AdminPushBroadcast['data']): string => {
  if (data?.action === 'navigate') {
    const label = SCREENS.find(([v]) => v === data.screen)?.[1] ?? data.screen;
    return `Go to screen · ${label}`;
  }
  if (data?.action === 'open_url') return 'Open URL';
  return 'Just open app';
};
const sentAt = (iso: string) => withTime(iso, { day: 'numeric', month: 'short' });

const GENDER_LABEL: Record<Gender, string> = { '': 'All', male: 'Male', female: 'Female' };
const SUB_LABEL: Record<Sub, string> = { '': 'All', subscribed: 'Subscribed', unsubscribed: 'Free' };

// Design `.radios` option row: separate 44px choice buttons that share the row, the chosen one outlined in navy.
function RadioButtons<K extends string>({ label, value, onChange, options }: { label: string; value: K; onChange: (k: K) => void; options: { key: K; label: string }[] }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button key={o.key} type="button" aria-pressed={on} onClick={() => onChange(o.key)}
            className={`min-h-[44px] min-w-[84px] flex-1 rounded-[12px] border text-[13.5px] font-semibold transition-colors ${on ? 'border-navy bg-surface-2 text-ink ring-1 ring-inset ring-navy' : 'border-line-strong bg-surface text-muted hover:text-ink'}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Design `.lb` label with the mono `.cc` counter on the same line. */
function LabelRow({ htmlFor, label, count }: { htmlFor: string; label: string; count?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-ink">{label}</label>
      {count && <span className="text-right font-mono text-xs font-medium text-muted">{count}</span>}
    </div>
  );
}

const LB = 'text-[13px] font-semibold text-ink';
/** Design `.sl` section label. */
const SL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

/** "Monday, 12 October" for the lock-screen date. */
function lockDate(now: Date): string {
  const wd = now.toLocaleDateString('en-GB', { weekday: 'long' });
  const rest = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  return `${wd}, ${rest}`;
}

const AdminNotification: React.FC = () => {
  const qc = useQueryClient();
  const { username: adminUsername } = useAuth();
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('');
  const [historyPage, setHistoryPage] = useState(1);
  const [targetCategory, setTargetCategory] = useState<TargetCategory>('all');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [action, setAction] = useState<Action>('');
  const [url, setUrl] = useState('');
  const [screen, setScreen] = useState('');
  const [username, setUsername] = useState('');
  const [gender, setGender] = useState<Gender>('');
  const [subscription, setSubscription] = useState<Sub>('');
  const [gradeId, setGradeId] = useState<number | ''>('');
  const [yearId, setYearId] = useState<number | ''>('');
  const [selectedInst, setSelectedInst] = useState<number | ''>('');
  const [selectedField, setSelectedField] = useState<number | ''>('');

  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [pending, setPending] = useState<{ payload: AdminNotificationPayload; summary: string; test?: boolean } | null>(null);

  const instQ = useQuery({
    queryKey: ['admin-institutions-basic'],
    queryFn: adminListInstitutionsBasic,
  });

  const institutions = instQ.data;
  const filteredInstitutions = useMemo(() => {
    if (!institutions) return [];
    if (targetCategory === 'schools') return institutions.filter(i => i.inst_type === 'school');
    if (targetCategory === 'universities') return institutions.filter(i => i.inst_type === 'college' || i.inst_type === 'university');
    return institutions;
  }, [institutions, targetCategory]);

  const gradesQ = useQuery({
    queryKey: ['admin-school-grades', selectedInst],
    queryFn: () => adminGetSchoolGrades(selectedInst as number),
    enabled: !!selectedInst && targetCategory === 'schools',
  });

  const fieldsQ = useQuery({
    queryKey: ['admin-fields'],
    queryFn: () => adminGetFieldOfStudy(),
    enabled: targetCategory === 'universities',
  });

  const yearsQ = useQuery({
    queryKey: ['admin-years', selectedField],
    queryFn: () => adminGetAcademicYears(selectedField as number),
    enabled: !!selectedField && targetCategory === 'universities',
  });

  const grades = gradesQ.data;
  const fields = fieldsQ.data;
  const years = yearsQ.data;
  const audienceError = instQ.isError || gradesQ.isError || fieldsQ.isError || yearsQ.isError;
  const retryAudience = () => {
    for (const q of [instQ, gradesQ, fieldsQ, yearsQ]) if (q.isError) q.refetch();
  };

  const historyQ = useQuery({
    queryKey: [HISTORY_KEY, historyFilter, historyPage],
    queryFn: () => adminGetPushBroadcasts({ status: historyFilter, page: historyPage, page_size: HISTORY_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const history = historyQ.data;
  const historyPages = Math.max(1, Math.ceil((history?.count ?? 0) / HISTORY_PAGE_SIZE));

  // The test flag travels with the call, so cancelling the dialog mid-send cannot turn a test into a normal send.
  const sendMutation = useMutation({
    mutationFn: ({ payload }: { payload: AdminNotificationPayload; test: boolean }) => adminSendNotification(payload),
    // Success or failure, the attempt is recorded server-side, so refresh the history.
    onSettled: () => { qc.invalidateQueries({ queryKey: [HISTORY_KEY] }); },
    onSuccess: (data, { test: isTest }) => {
      const reached = (data as any)?.tokens_targeted;
      const n = data?.users_matched ?? 0;
      const text = isTest
        ? `Sent a test to you. Delivery stats appear in the history below.${reached === 0 ? ' No device of yours is registered, so nothing will arrive. Sign in to the YADA app first.' : ''}`
        : `Sent to ${n} ${n === 1 ? 'user' : 'users'}. Delivery stats appear in the history below.`;
      setResult({ ok: true, text });
      toast.success(isTest ? 'Test notification sent to you.' : `Notification sent successfully! Targeted ${n} ${n === 1 ? 'user' : 'users'}.`);
      // A test keeps the draft so it can be sent for real.
      if (!isTest) { setTitle(''); setBody(''); }
      setPending(null);
    },
    onError: (error: any, { test: isTest }) => {
      const detail = error?.response?.data?.detail || 'Failed to send notification';
      const text = isTest ? `${detail} Sign in to the YADA app on a device first so it can receive a test.` : detail;
      setResult({ ok: false, text });
      toast.error(text);
      setPending(null);
    },
  });

  const instName = filteredInstitutions.find(i => i.id === selectedInst)?.name;
  const gradeName = grades?.find(g => g.id === gradeId)?.name;
  const fieldName = fields?.find(f => f.id === selectedField)?.name;
  const yearName = years?.find(y => y.id === yearId)?.name;

  // Built from the payload that will actually be sent, so the dialog can never
  // understate the audience.
  const summarize = (p: AdminNotificationPayload): string => {
    let base = 'every app user';
    if (p.username) base = p.username;
    else if (p.institution_id || p.school_grade_id || p.field_of_study_id || p.academic_year_id) {
      base = [p.institution_id ? instName : undefined, p.school_grade_id ? gradeName : undefined, p.field_of_study_id ? fieldName : undefined, p.academic_year_id ? yearName : undefined]
        .filter(Boolean).join(' · ') || 'selected group';
    }
    const quals = [p.gender, p.subscription_status === 'unsubscribed' ? 'free' : p.subscription_status].filter(Boolean);
    return [base, ...quals].join(' · ');
  };

  // Every line of the confirm body comes from the frozen pending payload, so
  // editing the form while the dialog is open cannot change what is confirmed.
  const tapTextFor = (p: AdminNotificationPayload) => {
    const d = p.data;
    if (d?.action === 'open_url') return `Opens ${d.url}`;
    if (d?.action === 'navigate') return `Opens screen "${d.screen}"`;
    return 'Just opens the app';
  };
  const confirmBody = pending
    ? `Audience: ${pending.summary}. Gender: ${GENDER_LABEL[(pending.payload.gender ?? '') as Gender]}. Subscription: ${SUB_LABEL[(pending.payload.subscription_status ?? '') as Sub]}. When tapped: ${tapTextFor(pending.payload)}. This sends a real push notification to every matching device.`
    : '';

  // "Send test to me" goes through the same confirm dialog, addressed to the signed-in admin only.
  const handleTestToMe = () => {
    setResult(null);
    if (!adminUsername) return;
    if (!title.trim() || !body.trim()) { setFormError('Title and message are required'); return; }
    if (action === 'open_url' && !/^https?:\/\/\S+$/i.test(url.trim())) { setFormError('Enter a link starting with http:// or https://'); return; }
    if (action === 'navigate' && !screen) { setFormError('Choose the screen to open'); return; }
    setFormError(null);
    const payload: AdminNotificationPayload = {
      title,
      body,
      username: adminUsername,
      data: action === 'open_url' ? { action, url: url.trim() } : action === 'navigate' ? { action, screen } : undefined,
    };
    setPending({ payload, summary: `${adminUsername} (test to you)`, test: true });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);
    // Submitting only ever opens the confirm dialog; the send happens on confirm.
    if (!title.trim() || !body.trim()) { setFormError('Title and message are required'); return; }
    if (targetCategory === 'individual' && !username.trim()) { setFormError('Username is required for individual targeting'); return; }
    if (targetCategory === 'schools' && !selectedInst) { setFormError('Pick a school'); return; }
    if (targetCategory === 'universities' && !selectedInst) { setFormError('Pick an institution'); return; }
    if (action === 'open_url' && !/^https?:\/\/\S+$/i.test(url.trim())) { setFormError('Enter a link starting with http:// or https://'); return; }
    if (action === 'navigate' && !screen) { setFormError('Choose the screen to open'); return; }
    setFormError(null);

    const payload: AdminNotificationPayload = {
      title,
      body,
      gender: gender || undefined,
      subscription_status: subscription || undefined,
      data: action === 'open_url' ? { action, url: url.trim() } : action === 'navigate' ? { action, screen } : undefined,
    };
    if (targetCategory === 'individual') {
      payload.username = username.trim();
    } else {
      if (selectedInst) payload.institution_id = selectedInst as number;
      if (gradeId) payload.school_grade_id = gradeId as number;
      if (selectedField) payload.field_of_study_id = selectedField as number;
      if (yearId) payload.academic_year_id = yearId as number;
    }
    setPending({ payload, summary: summarize(payload) });
  };

  const confirmSend = () => {
    if (pending && !sendMutation.isPending) sendMutation.mutate({ payload: pending.payload, test: !!pending.test });
  };

  const handleCategoryChange = (cat: TargetCategory) => {
    setTargetCategory(cat);
    setSelectedInst('');
    setSelectedField('');
    setGradeId('');
    setYearId('');
    setUsername('');
  };

  const devices = historyQ.data?.devices_registered;
  const [today] = useState(() => new Date());

  return (
    <div>
      <PageHeader
        eyebrow="Platform admin · Messaging"
        title="Push notifications"
        subtitle={typeof devices === 'number'
          ? `Reach students on the YADA app · ${devices.toLocaleString()} ${devices === 1 ? 'device' : 'devices'} registered. You confirm the audience before anything is sent.`
          : 'Broadcast messages to your mobile app users. You confirm the audience before anything is sent.'}
      />

      <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 items-start gap-[18px] min-[1181px]:grid-cols-12">
        <PanelCard title="Compose" className="min-[1181px]:col-span-7">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <LabelRow htmlFor="push-title" label="Notification title" count={`${title.length}/${TITLE_MAX}`} />
              <input id="push-title" type="text" maxLength={TITLE_MAX} placeholder="Catchy headline here…" value={title}
                onChange={(e) => setTitle(e.target.value)} className={INPUT} />
            </div>

            <div className="flex flex-col gap-1.5">
              <LabelRow htmlFor="push-body" label="Message" count={`${body.length}/${BODY_MAX}`} />
              <textarea id="push-body" rows={4} maxLength={BODY_MAX} placeholder="What do you want to say to your users?" value={body}
                onChange={(e) => setBody(e.target.value)} className={`${INPUT} min-h-[96px] py-3 leading-[1.45]`} />
            </div>

            <div className="flex flex-col gap-1.5">
              <p className={LB}>When tapped</p>
              <RadioButtons<Action>
                label="When tapped"
                value={action}
                onChange={(a) => setAction(a)}
                options={[{ key: '', label: 'Just open app' }, { key: 'open_url', label: 'Open URL' }, { key: 'navigate', label: 'Go to screen' }]}
              />
            </div>
            {action === 'open_url' && (
              <FormField label="Link / URL" htmlFor="push-url">
                <input id="push-url" type="text" placeholder="https://play.google.com/…" value={url} onChange={(e) => setUrl(e.target.value)} className={INPUT} />
              </FormField>
            )}
            {action === 'navigate' && (
              <FormField label="Screen" htmlFor="push-screen">
                <select id="push-screen" value={screen} onChange={(e) => setScreen(e.target.value)} className={INPUT}>
                  <option value="">Choose screen</option>
                  {SCREENS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </FormField>
            )}

            <div className="mt-1 flex flex-col gap-3.5 border-t border-hairline pt-[18px]">
              <p className={SL}>Audience</p>
              <RadioButtons<TargetCategory>
                label="Audience"
                value={targetCategory}
                onChange={handleCategoryChange}
                options={[
                  { key: 'all', label: 'All users' },
                  { key: 'schools', label: 'Schools' },
                  { key: 'universities', label: 'Universities' },
                  { key: 'individual', label: 'One user' },
                ]}
              />

              {targetCategory === 'individual' && (
                <FormField label="Username" htmlFor="push-username">
                  <input id="push-username" type="text" placeholder="Enter exact username…" value={username} onChange={(e) => setUsername(e.target.value)} className={INPUT} />
                </FormField>
              )}

              {(targetCategory === 'schools' || targetCategory === 'universities') && audienceError && (
                <ErrorPanel title="Couldn't load the audience lists" body="The schools, grades or fields could not be loaded." onRetry={retryAudience} />
              )}

              {(targetCategory === 'schools' || targetCategory === 'universities') && !audienceError && (
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  <FormField label={targetCategory === 'schools' ? 'School' : 'Institution'} htmlFor="push-inst">
                    <select id="push-inst" value={selectedInst} className={INPUT}
                      onChange={(e) => { setSelectedInst(e.target.value === '' ? '' : Number(e.target.value)); setGradeId(''); }}>
                      <option value="">{targetCategory === 'schools' ? 'Select a school' : 'Select an institution'}</option>
                      {filteredInstitutions.map(inst => <option key={inst.id} value={inst.id}>{inst.name}</option>)}
                    </select>
                  </FormField>

                  {targetCategory === 'schools' && selectedInst && (
                    <div className="flex flex-col gap-1.5 sm:col-span-2">
                      <p className={LB}>Grade level</p>
                      <PillTabs dense scroll label="Grade level" value={gradeId === '' ? '' : String(gradeId)}
                        onChange={(k) => setGradeId(k ? Number(k) : '')}
                        options={[{ key: '', label: 'All grades' }, ...(grades ?? []).map((g) => ({ key: String(g.id), label: g.name }))]} />
                    </div>
                  )}

                  {targetCategory === 'universities' && (
                    <>
                      <FormField label="Field of study" htmlFor="push-field">
                        <select id="push-field" value={selectedField} className={INPUT}
                          onChange={(e) => { setSelectedField(e.target.value ? Number(e.target.value) : ''); setYearId(''); }}>
                          <option value="">All Fields</option>
                          {fields?.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      </FormField>
                      {selectedField && (
                        <div className="flex flex-col gap-1.5 sm:col-span-2">
                          <p className={LB}>Academic year</p>
                          <PillTabs dense scroll label="Academic year" value={yearId === '' ? '' : String(yearId)}
                            onChange={(k) => setYearId(k ? Number(k) : '')}
                            options={[{ key: '', label: 'All years' }, ...(years ?? []).map((y) => ({ key: String(y.id), label: y.name }))]} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <p className={LB}>Gender</p>
                  <div><Segmented<Gender> label="Gender" value={gender} onChange={setGender}
                    options={[{ key: '', label: 'All' }, { key: 'male', label: 'Male' }, { key: 'female', label: 'Female' }]} /></div>
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                  <p className={LB}>Subscription</p>
                  <div><Segmented<Sub> label="Subscription" value={subscription} onChange={setSubscription}
                    options={[{ key: '', label: 'All' }, { key: 'subscribed', label: 'Subscribed' }, { key: 'unsubscribed', label: 'Free' }]} /></div>
                </div>
              </div>
              <p className="text-[13px] text-muted">Filters are combined: Schools + Female + Subscribed targets only subscribed female students in schools.</p>
            </div>

            {formError && <p role="alert" className="text-sm text-danger-ink">{formError}</p>}

            <div className="flex flex-wrap justify-end gap-2.5 border-t border-hairline pt-[18px]">
              <Button type="button" variant="ghost" onClick={handleTestToMe} disabled={!adminUsername || sendMutation.isPending}>Send test to me</Button>
              <Button type="submit" variant="secondary" loading={sendMutation.isPending} icon={<Send size={16} aria-hidden />}>Send notification</Button>
            </div>
          </div>
        </PanelCard>

        <section aria-label="Live preview" className="flex min-w-0 flex-col gap-3.5 min-[1181px]:col-span-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">Live preview</h2>
            <span className="text-[13px] text-muted">Lock screen · Android</span>
          </div>
          <div data-testid="push-preview" className="mx-auto w-[300px] max-w-full rounded-[44px] bg-phone-frame p-3 shadow-[0_30px_60px_-30px_theme(colors.navy/60%)]">
            <div className="min-h-[560px] rounded-[34px] bg-[linear-gradient(170deg,theme(colors.phone-top),theme(colors.navy)_55%,theme(colors.phone-bottom))] px-3.5 py-[18px] text-white">
              <div aria-hidden="true" className="mx-auto mb-7 h-6 w-24 rounded-full bg-phone-frame" />
              <p aria-hidden="true" className="text-center font-display text-[58px] font-semibold leading-none tracking-[-0.03em]">9:41</p>
              <p className="mb-7 mt-1.5 text-center text-sm text-on-navy">{lockDate(today)}</p>
              <div className="flex items-start gap-2.5 rounded-[20px] bg-white/90 px-3.5 py-3 text-navy">
                <img src={LogoMark} alt="" className="h-[34px] w-[34px] flex-none rounded-[9px]" />
                <span className="min-w-0 flex-1">
                  <span className="flex justify-between gap-2 text-xs text-navy/70"><span className="font-semibold">YADA</span><span>now</span></span>
                  <span className="mt-0.5 block break-words text-sm font-bold">{title || 'Notification title'}</span>
                  <span className="mt-0.5 block break-words text-[13px] leading-[1.4]">{body || 'Your message appears here.'}</span>
                </span>
              </div>
              <div aria-hidden="true" className="-mt-2 h-5 scale-[.94] rounded-b-[18px] bg-white/60 opacity-60" />
            </div>
          </div>
          {result && (
            <div role={result.ok ? 'status' : 'alert'} className={`flex items-start gap-2.5 rounded-[14px] px-3.5 py-3 text-[13px] ${result.ok ? 'bg-success-bg text-success' : 'bg-danger-bg text-danger-ink'}`}>
              {result.ok && <Check size={16} aria-hidden className="mt-0.5 flex-none" />}
              <span>{result.text}</span>
            </div>
          )}
        </section>
      </form>

      <PanelCard
        title="Send history"
        className="mt-[18px]"
        actions={(
          <Segmented<HistoryFilter>
            label="History filter"
            value={historyFilter}
            onChange={(f) => { setHistoryFilter(f); setHistoryPage(1); }}
            options={[
              { key: '', label: 'All' },
              { key: 'sent', label: 'Sent' },
              { key: 'partial', label: 'Partial' },
              { key: 'failed', label: 'Failed' },
              { key: 'no_recipients', label: 'No recipients' },
            ]}
          />
        )}
      >
        {historyQ.isLoading ? (
          <LoadingState label="Loading history…" />
        ) : historyQ.isError ? (
          <ErrorPanel title="Couldn't load the send history" body="The notification history could not be loaded." onRetry={() => historyQ.refetch()} />
        ) : !history || history.results.length === 0 ? (
          <EmptyState icon={<Bell size={18} />} title="No notifications sent yet" body={historyFilter ? 'Nothing matches this filter.' : undefined} />
        ) : (
          <>
            <Table minWidth={820} data-testid="push-history">
              <thead>
                <tr>
                  <Th>Notification</Th>
                  <Th>Audience</Th>
                  <Th>Sent</Th>
                  <Th align="right">Delivered</Th>
                  <Th align="right">Failed</Th>
                  <Th>Status</Th>
                  <Th>Sent by</Th>
                </tr>
              </thead>
              <tbody>
                {history.results.map((b) => (
                  <tr key={b.id}>
                    <Td>
                      <span className="block font-semibold text-ink">{b.title}</span>
                      <span className="block text-xs text-muted">{actionLine(b.data)}</span>
                    </Td>
                    <Td>{b.audience_label}</Td>
                    <Td className="whitespace-nowrap font-mono text-muted">{sentAt(b.created)}</Td>
                    <Td align="right" className="whitespace-nowrap font-mono"><span title={`of ${b.tokens_targeted.toLocaleString()} devices targeted`}>{b.success_count.toLocaleString()}</span></Td>
                    <Td align="right" className="font-mono">{b.failure_count.toLocaleString()}</Td>
                    <Td><Badge tone={STATUS_BADGE[b.status]?.tone ?? 'neutral'}>{STATUS_BADGE[b.status]?.label ?? b.status}</Badge></Td>
                    <Td className="text-muted">{b.sent_by_username || '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <PagedFooter divider="list" from={(historyPage - 1) * HISTORY_PAGE_SIZE + 1}
              to={(historyPage - 1) * HISTORY_PAGE_SIZE + history.results.length} total={history.count}
              page={historyPage} pageCount={historyPages} onPage={setHistoryPage} label="History pagination" />
          </>
        )}
      </PanelCard>

      <ConfirmDialog
        isOpen={pending !== null}
        title={`Send to ${pending?.summary ?? ''}?`}
        body={confirmBody}
        confirmLabel="Send now"
        danger={false}
        busy={sendMutation.isPending}
        onConfirm={confirmSend}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

export default AdminNotification;
